<template>
  <div v-if="displayWorkflowFallbackMessage" data-qa="preview-content-inbox">
    <div class="fallback-msg-ct">
      {{ $t('CampaignForm_Preview_ContentMessageDetails_NoMessages') }}
    </div>
  </div>
  <div v-else data-qa="preview-content-inbox">
    <inbox-test-send-dialog
      v-if="showSendPreview"
      :dialog-visible.sync="showSendPreview"
      :preview="previewSendData"
    />
    <div v-loading="loading" class="preview-body" data-qa="preview-inbox-body">
      <div
        class="inbox-preview-container"
        :class="{ 'inbox-preview-container--split': hasSidePanel }"
        data-qa="preview-inbox-device"
      >
        <inbox-message-preview
          :message="content"
          :message-type="messageType"
          :message-style="messageStyle"
          :global-styles="globalStyles"
        />
      </div>
      <div
        v-if="hasSidePanel"
        class="preview-details"
        data-qa="preview-inbox-side-panel"
      >
        <preview-default-action
          :url="defaultActionUrl"
          :deeplink="defaultActionDeeplink"
          :action-type="defaultActionType"
          data-qa-prefix="preview-inbox"
        />
        <preview-payload :items="payloadMap" data-qa-prefix="preview-inbox" />
      </div>
    </div>
  </div>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import { PreviewChannelEnum } from '@/modules/campaign-manage/enum/PreviewChannelEnum'
import { EventBus } from '@/core/EventBus'
import { Message } from 'element-ui'
import _ from 'lodash'
import InboxMessagePreview from '@/common/components/inbox/InboxMessagePreview.vue'
import PreviewDefaultAction from '@/modules/campaign-manage/components/preview/PreviewDefaultAction.vue'
import PreviewPayload from '@/modules/campaign-manage/components/preview/PreviewPayload.vue'
import PreviewPayloadMapMixin from '@/modules/campaign-manage/mixins/PreviewPayloadMapMixin'
import InboxTestSendDialog from '@/modules/campaign-manage/components/preview/InboxTestSendDialog.vue'
import {
  processCustomAttributes,
  normaliseUnmetConditions
} from '@/modules/campaign-manage/helpers/PreviewAttributeHelper'

export default {
  name: 'PreviewContentInbox',
  mixins: [PreviewPayloadMapMixin],
  components: {
    InboxMessagePreview,
    PreviewDefaultAction,
    PreviewPayload,
    InboxTestSendDialog
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    variantKey: {
      type: String,
      required: true
    },
    workflowAction: {
      type: Object,
      required: false,
      default: () => ({})
    },
    language: {
      type: String,
      required: false
    },
    profileId: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Full profile object corresponding to `profileId` — used by the test-send
     * dialog to render a human label for the selected profile in its description.
     */
    selectedProfile: {
      type: Object,
      required: false,
      default: null
    },
    customAttributes: {
      type: Object,
      required: false,
      default: null
    }
  },
  mounted() {
    EventBus.$on('showSendPreview', this.onShowSendPreview)
    if (this.displayWorkflowFallbackMessage) {
      this.loading = false
      EventBus.$emit('set-profile-attributes', {})
      EventBus.$emit('set-unmet-attribute-conditions', {})
      return
    }
    this.getPreview()
  },
  beforeDestroy() {
    EventBus.$off('showSendPreview', this.onShowSendPreview)
  },
  data() {
    return {
      loading: true,
      content: {},
      showSendPreview: false
    }
  },
  computed: {
    style() {
      return (this.content && this.content.style) || {}
    },
    messageType() {
      return this.style.type || 0
    },
    messageStyle() {
      return {
        bg: this.style.bg,
        title_bg: this.style.title_bg
      }
    },
    globalStyles() {
      return {}
    },
    displayWorkflowFallbackMessage() {
      return Boolean(this.model.workflow) && _.isEmpty(this.workflowAction)
    },
    defaultActionUrl() {
      return this.content.default_action_url || ''
    },
    defaultActionDeeplink() {
      return this.content.default_action_deeplink || ''
    },
    defaultActionType() {
      const type = this.content.default_action_type
      return type && type !== 'none' ? type : null
    },
    hasSidePanel() {
      return (
        !!this.defaultActionUrl ||
        !!this.defaultActionDeeplink ||
        !!this.defaultActionType ||
        this.payloadMap.length > 0
      )
    },
    previewSendData() {
      return {
        campaign: this.model,
        message_type: channelTypes.inbox,
        variant_key: this.variantKey,
        workflow_action: this.workflowAction,
        language: this.language,
        profile_id: this.profileId,
        selected_profile: this.selectedProfile,
        customAttributes: this.processCustomAttributes(this.customAttributes)
      }
    }
  },
  watch: {
    customAttributes: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.getPreview()
        }
      },
      deep: true
    }
  },
  methods: {
    processCustomAttributes,
    onShowSendPreview(payload) {
      if (!payload || payload.channel === PreviewChannelEnum.INBOX) {
        this.showSendPreview = true
      }
    },
    getPreview() {
      this.loading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaign-messages/preview`,
          data: {
            campaign: this.model,
            message_type: channelTypes.inbox,
            variant_key: this.variantKey,
            workflow_action: this.workflowAction,
            language: this.language,
            profile_id: this.profileId,
            customAttributes: this.processCustomAttributes(
              this.customAttributes
            )
          }
        })
      )
        .then(({ data }) => {
          if (this._isDestroyed) return
          this.content = data['message']
          EventBus.$emit('set-profile-attributes', data.attributes)
          if (!this.customAttributes && this.profileId) {
            EventBus.$emit('set-profile-id', this.profileId)
          }
          EventBus.$emit(
            'set-unmet-attribute-conditions',
            normaliseUnmetConditions(data['unmetConditions'])
          )
        })
        .catch(error => {
          Message.closeAll()
          this.$message({
            message: error.message,
            type: 'error',
            duration: 5000
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.fallback-msg-ct {
  margin-top: 30%;
  margin-right: 50%;
  margin-left: 50%;
  width: 300px;
  font-size: 16px;
}

.preview-body {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.inbox-preview-container {
  display: flex;
  justify-content: center;
  padding: 30px 0;
  width: 100%;
}

.inbox-preview-container--split {
  width: 50%;
}

.preview-details {
  width: 50%;
  padding-top: 20px;
  font-size: 12px;
  color: #606266;
  min-width: 0;
}

.side-panel-section {
  padding-top: 4px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/PreviewContentInbox.vue